import { observer } from "mobx-react";
import { toNumber } from "lodash";
import {
  creatHubConnection,
  currencyFormatter,
  formatDate,
  getDisplayDeliveryType,
  getDisplayPaymentStatusForOrder,
  getOrderDisplayStatusForOrder,
  isPaymentFailedOrder,
  ShowAlert,
  showConfirmationDialog,
  zeroPad,
} from "../../utils/functions";
import { IOrder, IUpdateOrderStatusResponse } from "../../stores/order";
import { useAuth } from "../../contexts/auth-context";
import {
  ApiResponse,
  DeliveryMethod,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
  StripeRefundStatusEnum,
} from "../../stores/index.d";
import { useStores } from "../../stores/root-store";
import { useCallback, useEffect, useState } from "react";
import OderStatusDdl from "./order-status-ddl";
import OrderDetails from "./order-details";
import { useSearchParams } from "react-router-dom";
import { IStripeSessionResponse } from "../../stores/cart";
import { useCookies } from "react-cookie";
import OrderRefund from "./order-refund";
import DeliveryDriver from "./delivery-driver";
import useSound from "use-sound";
import dingSound from "../../resources/sounds/ding.mp3";
import { ApiConfig } from "../../services/api-config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUtensils } from "@fortawesome/free-solid-svg-icons";

interface IOrderListing {
  orders?: IOrder[];
  // ManageOrders owns one persistent SignalR listener so changing tabs cannot
  // interrupt incoming-order updates or create duplicate print listeners.
  orderEventsManagedByParent?: boolean;
}

const OrderListing = ({
  orders,
  orderEventsManagedByParent,
}: IOrderListing) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isShopUser, isShopAdmin, userId } = useAuth();
  const { orderStore } = useStores();
  const {
    updateOrderStatus,
    markAsPaymentPaid,
    takePayment,
    fetchOrders,
    fetchCustomerOrders,
    refreshRefundStatus,
    receiveNewOrders,
  } = orderStore;
  const [viewOrder, setViewOrder] = useState<IOrder>();
  const [cookies, setCookie, removeCookie] = useCookies();
  const [showOrderRefundModal, setShowOrderRefundModal] = useState(false);
  const [refundOrder, setRefundOrder] = useState<IOrder>();
  const [showDeliveryDriverModal, setShowDeliveryDriverModal] = useState({
    show: false,
    orderId: 0,
  });

  const [notifiy, setNotifiy] = useState(false);
  const [playSound] = useSound(dingSound);

  const inProgStatusList = [
    { text: "Ready For Collection", value: OrderStatus.ReadyForCollection },
    { text: "Out For Delivery", value: OrderStatus.OutForDelivery },
    { text: "Completed", value: OrderStatus.Completed },
  ];

  const newOrderStatusList = [
    { text: "Accept Order", value: OrderStatus.InProgress },
    { text: "Cancel Order", value: OrderStatus.Cancelled },
  ];

  const orderHubCallback = useCallback(
    (order: IOrder) => {
      if (order) {
        receiveNewOrders(order);
        if (isShopAdmin || isShopUser) setNotifiy(true);
      }
    },
    [isShopAdmin, isShopUser, receiveNewOrders]
  );

  useEffect(() => {
    if (notifiy) {
      playSound();
      setNotifiy(false);
    }
  }, [notifiy, playSound]);

  useEffect(() => {
    if (orderEventsManagedByParent) return undefined;

    const connection = creatHubConnection(
      ApiConfig.REST_URIS.order.ordersHub,
      "ReceiveOrder",
      orderHubCallback
    );
    // Use the connection captured by this effect closure (not the state
    // variable) so the connection is actually stopped on unmount instead of
    // leaking/duplicating listeners across remounts.
    return () => {
      connection?.stop();
    };
  }, [orderEventsManagedByParent, orderHubCallback]);

  useEffect(() => {
    if (!orders || !orders.length) return;
    const orderId = toNumber(searchParams.get("id"));
    const order = orders?.find((p) => p.orderId == orderId);
    if (order) {
      handleViewOrder(order);
    }
  }, [searchParams, orders]);

  useEffect(() => {
    // Page owners poll ALL orders. A tab/pagination remount must never make a
    // payment request, including when a checkout-return cookie is still present.
    const paidOrder = orderStore.orders.find(order =>
      order.orderId === Number(cookies.pyOrderId) &&
      order.stripeSessionId === cookies.pyId &&
      order.paymentStatus === PaymentStatus.Paid);
    if (cookies.pyId && paidOrder) {
      removeCookie("pyId", { path: "/" });
      removeCookie("pyOrderId", { path: "/" });
    }
  }, [orderStore.orders, cookies.pyId, cookies.pyOrderId, removeCookie]);

  const updateOrderStatusCallback = (
    success: boolean,
    resData: IUpdateOrderStatusResponse,
    statusId: number
  ) => {
    if (success && resData != null && resData.orderId > 0) {
      if (statusId != OrderStatus.Cancelled || resData.refundResponse == null) {
        ShowAlert.Success(
          `Order #${zeroPad(resData.orderId, 8)} ${
            statusId == OrderStatus.Cancelled
              ? "has been cancelled"
              : "status has been updated"
          }`
        );
      } else {
        const { status, isFailed, failedReason } = resData.refundResponse;
        if (isFailed) {
          ShowAlert.Error(failedReason);
        } else {
          ShowAlert.Success(
            "Order cancelled and refund request created for Order # " +
              zeroPad(resData.orderId, 8)
          );

          if (StripeRefundStatusEnum.Pending == status)
            refreshRefundStatus(resData.orderId);
        }
      }

      if (isShopAdmin || isShopUser) fetchOrders();
      else fetchCustomerOrders(userId);
    } else
      ShowAlert.Error(
        `Order #${zeroPad(
          resData.orderId,
          8
        )} status failed to update. Please try again.`
      );
  };

  const handleStatusChange = (
    orderId: number,
    statusId: number,
    paymentStatusId: number
  ) => {
    if (
      statusId == OrderStatus.Completed &&
      paymentStatusId == PaymentStatus.Pending
    ) {
      ShowAlert.Error(
        "Can't complete the order when order payment is pending. Please mark as order paid and try again."
      );
      return;
    }

    const update = () => {
      updateOrderStatus(orderId, statusId, updateOrderStatusCallback);
    };

    if (statusId == OrderStatus.OutForDelivery) {
      setShowDeliveryDriverModal({ show: true, orderId });
      return;
    } else if (statusId == OrderStatus.Cancelled) {
      showConfirmationDialog(
        update,
        "Are you sure?",
        "Do you really want to cancel this order? This action cannot be undone!",
        "Yes, cancel it!"
      );
    } else if (statusId == OrderStatus.Completed) {
      showConfirmationDialog(
        update,
        "Are you sure?",
        "Do you really want to complete this order? This action cannot be undone!",
        "Yes, complete it!",
        "#198754"
      );
    } else {
      update();
    }
  };

  const handleViewOrder = (order?: IOrder) => {
    setViewOrder(order);

    if (!order) {
      if (searchParams.has("id")) {
        searchParams.delete("id");
        setSearchParams(searchParams);
      }
    }
  };

  const handleOrderMarkAsPaid = (orderId: number) => {
    const callback = (res: boolean) => {
      if (res) {
        fetchOrders();
        ShowAlert.Success(`Order #${zeroPad(orderId, 8)} has marked as paid.`);
      } else
        ShowAlert.Error(
          `Order #${zeroPad(
            orderId,
            8
          )} failed to marked as paid. Please try again.`
        );
    };
    showConfirmationDialog(
      () => markAsPaymentPaid(orderId, callback),
      "Confirm payment received",
      "Use this after cash or a card-machine payment has been successfully taken.",
      "Yes, mark paid",
      "#198754"
    );
  };

  const handleTakePayment = (orderId: number) => {
    takePayment(orderId, (res: ApiResponse<IStripeSessionResponse>) => {
      if (!res?.isSuccess || !res.data) {
        ShowAlert.Error(res?.message ?? "Unable to start payment. Please try again.");
        return;
      }

      if (res.data.isPaid) {
        fetchOrders();
        ShowAlert.Success(`Order #${zeroPad(orderId, 8)} is already paid.`);
        return;
      }

      if (!res.data.id || !res.data.stripeCheckOutUrl) {
        ShowAlert.Error("Payment page was not returned. Please try again.");
        return;
      }

      const expires = new Date(Date.now() + 15 * 60 * 1000);
      setCookie("pyId", res.data.id, { expires, path: "/" });
      setCookie("pyOrderId", orderId, { expires, path: "/" });
      window.location.assign(res.data.stripeCheckOutUrl);
    });
  };

  const handleCancelOrder = (orderId: number) => {
    showConfirmationDialog(
      () => {
        updateOrderStatus(
          orderId,
          OrderStatus.Cancelled,
          updateOrderStatusCallback
        );
      },
      "Are you sure?",
      "Do you really want to cancel this order? This action cannot be undone!",
      "Yes, cancel it!"
    );
  };

  const handlRefundOrder = (order: IOrder) => {
    setRefundOrder(order);
    setShowOrderRefundModal(order?.orderId > 0);
  };

  return (
    <div className="d-flex flex-column">
      {orders?.map((order: IOrder, i) => {
        const st = getOrderDisplayStatusForOrder(order);
        const pay = getDisplayPaymentStatusForOrder(order);
        const del = getDisplayDeliveryType(order.orderDeliveryType);
        const paymentFailed = isPaymentFailedOrder(order);
        const isOpenOrder =
          order.orderStatus === OrderStatus.Placed ||
          order.orderStatus === OrderStatus.InProgress;
        const canTakePayment =
          (isShopAdmin || isShopUser) &&
          isOpenOrder &&
          !paymentFailed &&
          order.paymentStatus === PaymentStatus.Pending;

        return (
          <div key={i} className="p-2 card mb-3 shadow-lg">
            <div className="card-body">
              {order.orderDeliveryType == DeliveryMethod.DineIn && (
                <>
                  <span className="position-absolute small h6 badge-table-number">
                    Table #{order.tableNumber}
                  </span>
                  <span className="position-absolute translate-middle badge rounded bg-success badge-order">
                    <FontAwesomeIcon className="fs-4" icon={faUtensils} />
                  </span>
                </>
              )}
              <div className="d-flex justify-content-between order-card-summary">
                <div className="d-flex flex-column">
                  <span
                    className="badge bg-primary p-2"
                    onClick={() => playSound()}
                  >
                    Order# {zeroPad(order.orderId, 8)}
                  </span>
                  <small className="mt-2">
                    Placed at {formatDate(order.orderDate)}
                  </small>
                </div>
                <div className="d-flex flex-column mb-3">
                  <span>
                    Payment:{" "}
                    <strong className={pay.cssClass}>{pay.text}</strong>
                  </span>
                  {order.paymentStatus === PaymentStatus.Pending &&
                    order.amountPaid > 0 && (
                      <small className="text-end">
                        Paid {currencyFormatter(order.amountPaid)} · Due{" "}
                        <strong>
                          {currencyFormatter(
                            Math.max(0, order.orderTotal - order.amountPaid)
                          )}
                        </strong>
                      </small>
                    )}
                  <span>
                    Order Type:{" "}
                    <strong className={del.cssClass}>{del.text}</strong>
                  </span>
                </div>
                <div className="d-flex flex-column mb-3">
                  <strong className={st.cssClass}>{st.text}</strong>
                  <strong>{currencyFormatter(order.orderTotal)}</strong>
                </div>
              </div>
              <div className="d-flex justify-content-between">
                {order.orderItems
                  .flatMap(
                    (it) => it.product.productName + " - " + it.quantity + "Qty"
                  )
                  .join(", ")}
                <div className="d-flex flex-wrap gap-2 align-self-baseline justify-content-end order-actions">
                  {canTakePayment && (
                    <>
                      <button
                        className="btn btn-primary m-0"
                        type="button"
                        onClick={() => handleTakePayment(order.orderId)}
                      >
                        <i className="fa fa-credit-card me-2"></i>
                        Take payment
                      </button>
                      <button
                        className="btn btn-success m-0"
                        type="button"
                        onClick={() => handleOrderMarkAsPaid(order.orderId)}
                      >
                        <i className="fa fa-check me-2"></i>
                        Mark paid
                      </button>
                    </>
                  )}
                  {order.orderStatus === OrderStatus.Placed &&
                  !paymentFailed ? (
                    isShopAdmin || isShopUser ? (
                      <OderStatusDdl
                        orderId={order.orderId}
                        currentStatus={st.text}
                        statusList={newOrderStatusList}
                        paymentStatusId={order.paymentStatus}
                        handleStatusChange={handleStatusChange}
                      />
                    ) : (
                      <button
                        className="btn btn-default m-0"
                        type="button"
                        onClick={() => handleCancelOrder(order.orderId)}
                      >
                        <i className="fa fa-times me-2"></i>
                        Cancel Order
                      </button>
                    )
                  ) : (
                    <>
                      {order.orderStatus !== OrderStatus.Completed &&
                        order.orderStatus !== OrderStatus.Cancelled &&
                        order.orderStatus !== OrderStatus.RefundInitiated &&
                        order.orderStatus !== OrderStatus.Refunded &&
                        !paymentFailed &&
                        (isShopAdmin || isShopUser) && (
                          <OderStatusDdl
                            orderId={order.orderId}
                            currentStatus={st.text}
                            paymentStatusId={order.paymentStatus}
                            statusList={inProgStatusList.filter((s) =>
                              order.orderStatus !== s.value &&
                              order.orderDeliveryType ===
                                DeliveryMethod.Collection
                                ? s.value != OrderStatus.OutForDelivery
                                : s.value != OrderStatus.ReadyForCollection
                            )}
                            handleStatusChange={handleStatusChange}
                          />
                        )}

                      {(isShopAdmin || isShopUser) &&
                        order.paymentMethod == PaymentMethod.Card &&
                        order.paymentStatus == PaymentStatus.Paid &&
                        order.orderStatus !== OrderStatus.RefundInitiated &&
                        order.orderStatus !== OrderStatus.Refunded && (
                          <button
                            className="btn btn-dark m-0"
                            type="button"
                            onClick={() => handlRefundOrder(order)}
                          >
                            Refund
                          </button>
                        )}
                    </>
                  )}

                  <button
                    className="btn btn-secondary m-0"
                    type="button"
                    onClick={() => handleViewOrder(order)}
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
      {!orders?.length && <h6 className="text-center">No orders available.</h6>}
      {orders != null && orders.length > 0 && viewOrder && (
        <OrderDetails
          order={viewOrder}
          show={viewOrder != null}
          setShowModal={() => handleViewOrder(undefined)}
        />
      )}

      {showOrderRefundModal && refundOrder && (
        <OrderRefund
          order={refundOrder}
          show={showOrderRefundModal}
          setShowModal={setShowOrderRefundModal}
        />
      )}

      {showDeliveryDriverModal.show && (
        <DeliveryDriver
          orderId={showDeliveryDriverModal.orderId}
          show={showDeliveryDriverModal.show}
          setShowModal={(show: boolean) =>
            setShowDeliveryDriverModal({ show, orderId: 0 })
          }
        />
      )}
    </div>
  );
};

export default observer(OrderListing);
